import * as Dialog from '@radix-ui/react-dialog'
import { useSelector } from '@tanstack/react-form'
import classNames from 'classnames'
import { Suspense, useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import { useToastService } from '../../services/toast-service.tsx'
import {
  usePostAnalyzeScan,
  usePostBeaconScan,
  usePostUploadSbom,
  usePostUploadScan,
  useSuspenseGetRepository,
} from '../../utils/api-client/api-hooks.ts'
import { isAiScannerTool } from '../../utils/detector-utils.ts'
import { initializeFirstBatch } from '../../utils/first-batch-tracking.ts'
import { ErrorBoundary } from '../error-boundaries/error-boundary'
import { Checkbox } from '../input/checkbox'
import { Spinner } from '../spinner.tsx'
import { Tooltip } from '../tooltip.tsx'
import * as styles from './add-findings-drawer.css.ts'
import {
  AddFindingsFooterActions,
  AddFindingsForm,
  type ProvideMethod,
  type SubmitPayload,
  useAddFindingsForm,
  useFormContext,
} from './add-findings-form.tsx'
import { DrawerRepositoryContext } from './drawer-repository-context.tsx'
import * as footerStyles from './floating-footer.css'
import { FloatingFooter } from './floating-footer.tsx'

export type AddFindingsDrawerProps = {
  repositoryId: string
  onCloseDrawer: () => unknown
}

export const AddFindingsDrawer: React.FC<AddFindingsDrawerProps> = ({ repositoryId, onCloseDrawer }) => {
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const [autoAnalyze, setAutoAnalyze] = useState(false)

  const { handleFormSubmit, isPending } = useAddFindingsMutations({
    repositoryId,
    onCloseDrawer,
    autoAnalyze,
  })

  const form = useAddFindingsForm()

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onCloseDrawer()
      }
    },
    [onCloseDrawer]
  )

  return (
    <Dialog.Root open={true} onOpenChange={handleOpenChange} modal={false}>
      <Dialog.Portal>
        <form.AppForm>
          <Dialog.Content
            ref={drawerContentRef}
            className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
            data-has-floating-footer={true}
            onPointerDownOutside={event => event.preventDefault()}
            onInteractOutside={event => event.preventDefault()}
          >
            <Suspense fallback={<Spinner size="64" variant="cool" />}>
              <AddFindingsDrawerBody repositoryId={repositoryId} />
            </Suspense>
          </Dialog.Content>
          <FloatingFooter drawerContentRef={drawerContentRef}>
            <AutoAnalyzeToggle repositoryId={repositoryId} autoAnalyze={autoAnalyze} onChange={setAutoAnalyze} />
            <div className={styles.actionButtons}>
              <AddFindingsFooterActions onCancel={onCloseDrawer} isPending={isPending} onSubmit={handleFormSubmit} />
            </div>
          </FloatingFooter>
        </form.AppForm>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const AddFindingsDrawerBody: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const { data: repository } = useSuspenseGetRepository({ repositoryId })
  return (
    <>
      <Dialog.Title className={styles.title}>
        <DrawerRepositoryContext type={repository.type} name={repository.name} />
        Add findings
      </Dialog.Title>
      <Dialog.Description className={styles.description}>
        Manually designate tool findings for analysis
      </Dialog.Description>
      <ErrorBoundary fallbackTitle="Unable to load upload form">
        <AddFindingsForm repositoryId={repositoryId} />
      </ErrorBoundary>
    </>
  )
}

type AutoAnalyzeToggleProps = {
  repositoryId: string
  autoAnalyze: boolean
  onChange: (checked: boolean) => unknown
}

const AUTO_ANALYZE_LABEL = 'Automatically analyze all findings'
const AUTO_ANALYZE_TOOLTIP =
  'It is recommended to manually select findings for analysis after upload, or to create an automated workflow'

const AutoAnalyzeToggle: React.FC<AutoAnalyzeToggleProps> = ({ repositoryId, autoAnalyze, onChange }) => {
  const form = useFormContext()
  const selectedTool = useSelector(form.store, s => s.values.selectedTool)

  const autoAnalyzeId = `autoAnalyze-addFindings-${repositoryId}`
  // A Beacon run always verifies and triages what it raises; there is nothing to opt into.
  if (selectedTool && isAiScannerTool(selectedTool)) {
    return null
  }
  return (
    <div className={styles.autoAnalyzeContainer}>
      <Checkbox
        id={autoAnalyzeId}
        checked={autoAnalyze}
        onCheckedChange={checked => onChange(checked === true)}
        ariaLabel={AUTO_ANALYZE_LABEL}
      />
      <label htmlFor={autoAnalyzeId} className={styles.autoAnalyzeLabel}>
        {AUTO_ANALYZE_LABEL}
      </label>
      <Tooltip label="Info about auto analysis" side="top" sideOffset={8} width={280}>
        {AUTO_ANALYZE_TOOLTIP}
      </Tooltip>
    </div>
  )
}

type UseAddFindingsMutationsArgs = {
  repositoryId: string
  onCloseDrawer: () => unknown
  autoAnalyze: boolean
}

type UseAddFindingsMutationsResult = {
  handleFormSubmit: (payload: SubmitPayload) => unknown
  isPending: boolean
}

const useAddFindingsMutations = ({
  repositoryId,
  onCloseDrawer,
  autoAnalyze,
}: UseAddFindingsMutationsArgs): UseAddFindingsMutationsResult => {
  const navigate = useNavigate()
  const { addToast } = useToastService()

  const autoAnalyzeRef = useRef(autoAnalyze)
  autoAnalyzeRef.current = autoAnalyze
  const effectiveProvideMethodRef = useRef<ProvideMethod>('upload')

  const navigateToScan = useCallback(
    (scanId: string) => {
      onCloseDrawer()
      navigate(Routes.ScanDetailsPage.createPath({ scanId }))
    },
    [onCloseDrawer, navigate]
  )

  const showAnalysisError = useShowMutationError({ prefix: 'Unable to trigger analysis' })
  const analyzeScanMutation = usePostAnalyzeScan({
    onSuccess: (_data, variables) => {
      addToast({ message: <>Analysis triggered</>, variant: 'success' })
      initializeFirstBatch(variables.scanId, 1)
      navigateToScan(variables.scanId)
    },
    onError: (error, variables, ...rest) => {
      showAnalysisError(error, variables, ...rest)
      navigateToScan(variables.scanId)
    },
  })

  const showUploadError = useShowMutationError()
  const uploadScanMutation = usePostUploadScan({
    onSuccess: ({ id: scanId }) => {
      addToast({
        message: (
          <>
            {effectiveProvideMethodRef.current === 'upload'
              ? 'File successfully uploaded'
              : 'Findings successfully imported'}
          </>
        ),
        variant: 'success',
      })
      if (autoAnalyzeRef.current) {
        analyzeScanMutation.mutate({ scanId })
      } else {
        navigateToScan(scanId)
      }
    },
    onError: showUploadError,
  })

  const showSbomError = useShowMutationError({ prefix: 'Unable to upload SBOM' })
  const uploadSbomMutation = usePostUploadSbom({
    onSuccess: () => {
      addToast({ message: <>SBOM uploaded successfully.</>, variant: 'success' })
    },
    onError: showSbomError,
  })

  // Pixee Beacon has no file to upload: it starts a run at the standard effort and opens the run's page.
  // A repository runs one at a time, so when one is already unfinished this opens that run instead.
  const showAiScanError = useShowMutationError({ prefix: 'Unable to start the Beacon scan' })
  const aiScanMutation = usePostBeaconScan({
    onSuccess: run => {
      addToast({ message: <>Beacon is scanning this repository.</>, variant: 'success' })
      onCloseDrawer()
      navigate(Routes.BeaconRunPage.createPath({ runId: run.id }))
    },
    onError: showAiScanError,
  })

  const handleFormSubmit = useCallback(
    (payload: SubmitPayload) => {
      if (isAiScannerTool(payload.scanBody.metadata.tool)) {
        aiScanMutation.mutate({ repositoryId, effort: 'medium' })
        return
      }
      effectiveProvideMethodRef.current = payload.effectiveProvideMethod
      uploadScanMutation.mutate({ repositoryId, body: payload.scanBody })
      if (payload.sbomBody) {
        uploadSbomMutation.mutate({ repositoryId, body: payload.sbomBody })
      }
    },
    [repositoryId, uploadScanMutation.mutate, uploadSbomMutation.mutate, aiScanMutation.mutate]
  )

  const isPending =
    uploadScanMutation.isPending ||
    uploadSbomMutation.isPending ||
    analyzeScanMutation.isPending ||
    aiScanMutation.isPending

  return { handleFormSubmit, isPending }
}
